import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { PageEvent } from '@angular/material/paginator';
import { MatSort, MatSortable, Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { MatTableExporterDirective } from 'mat-table-exporter';
import * as XLSX from 'xlsx-js-style';
import { ExportacaoDialogComponent, ExportacaoDialogData, ExportacaoDialogResult } from '../exportacao-dialog/exportacao-dialog.component';
import { ListaAcaoEngrenagem } from '../models/lista-acao-engrenagem.model';
import { ListaColunaConfig } from '../models/lista-coluna-config.model';
import { ListaParametrosPaginacao } from '../models/lista-paginacao.model';

export interface ExportacaoConfig {
  nomeArquivo: string;
  autor?: string;
  formato: 'xlsx' | 'csv';
}

/**
 * Grade de dados com mat-table, filtro textual local, paginação, ordenação,
 * exportação Excel/CSV e menu de ações por linha.
 *
 * Suporta paginação server-side (emite `paginacaoAlterada`) ou client-side.
 * Configuração de colunas via `ListaColunaConfig` com diversos modos de célula.
 */
@Component({
  selector: 'app-list-box',
  templateUrl: './list-box.component.html',
  styleUrls: ['./list-box.component.scss'],
})
export class ListBoxComponent implements OnInit, OnChanges {
  @Input() itens: unknown[] = [];
  @Input() totalRegistros = 0;
  @Input() paginaAtual = 1;
  @Input() tamanhoPagina = 10;
  @Input() opcoesTamanhoPagina: number[] = [10, 20, 50, 100];
  @Input() colunas: ListaColunaConfig[] = [];
  @Input() acoesEngrenagem: ListaAcaoEngrenagem<any>[] = [];
  @Input() idCampo = 'id';

  @Input() filtroTextoExibir = true;
  @Input() filtroTextoLabel: string | null = 'Filtrar';
  @Input() filtroTextoPlaceholder = 'Digite para filtrar...';

  @Input() paginarColunas = false;
  @Input() colunasPorGrupo = 6;

  /** Se true, usa paginação client-side (todo array em memória). */
  @Input() paginacaoClientSide = false;

  /** Condição para cor de linha (string avaliada como função). */
  @Input() corLinhaCondicao = '';

  /** Mostrar paginador. */
  @Input() mostrarPaginador = true;

  /** Mostrar combo "Ordenar por" + radio asc/desc. Default: true. */
  @Input() ordenacaoExibir = true;
  /**
   * Quando true, o ListBox emite `(ordenacaoAlterada)` para o pai
   * fazer a recarga server-side; o combo passa a mostrar apenas
   * colunas com `valorOrdenacaoBackend` preenchido. Quando false
   * (default), a ordenação é local (MatSort sobre os itens da página).
   */
  @Input() ordenacaoServerSide = false;

  @Output() paginacaoAlterada = new EventEmitter<ListaParametrosPaginacao>();
  @Output() botaoAcaoClicado = new EventEmitter<{ linha: unknown; coluna: ListaColunaConfig }>();
  /**
   * Emitido quando combo "Ordenar por", radio asc/desc ou clique no
   * cabeçalho (mat-sort-header) alteram a ordenação. O pai usa este
   * evento para recarregar os dados server-side.
   */
  @Output() ordenacaoAlterada = new EventEmitter<{
    idColuna: string;
    valorBackend?: string;
    desc: boolean;
  }>();
  
  /** Emitido quando o usuário escolhe exportar TODOS os dados (não apenas a página atual). */
  @Output() exportarTodosSolicitado = new EventEmitter<ExportacaoConfig>();

  /**
   * Emitido quando o usuário clica em um dos ícones de uma coluna do
   * tipo `'iconesImagens'`. O `iconeId` é o `id` configurado no item
   * (ex: 'BOMBA' / 'PANEL' / 'PLACA').
   */
  @Output() iconeImagemClicado = new EventEmitter<{
    linha: unknown;
    coluna: ListaColunaConfig;
    iconeId: string;
  }>();

  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild(MatTableExporterDirective, { static: false }) exporter!: MatTableExporterDirective;

  dataSource!: MatTableDataSource<unknown>;
  displayedColumns: string[] = [];
  filtroTextoInterno = '';
  indiceGrupoColuna = 0;
  /** Coluna selecionada no combo de ordenação. Sincronizada com `MatSort`. */
  ordenacaoColunaSelecionada = '';
  /** Direção da ordenação: false = ASC, true = DESC. */
  ordenacaoDesc = false;
  /**
   * Garante que `aplicarOrdenacaoPadrao` rode uma única vez (na primeira
   * vez que o `MatSort` ficar disponível). Sem essa flag, cada novo
   * `inicializarDataSource()` (toda vez que `itens` muda) reaplicava a
   * ordenação padrão, que disparava `(matSortChange)` → emitia
   * `(ordenacaoAlterada)` → o pai recarregava → loop infinito.
   */
  private ordenacaoInicializada = false;

  constructor(private dialog: MatDialog) {}

  ngOnInit(): void {
    this.inicializarDataSource();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['itens'] || changes['colunas']) {
      this.inicializarDataSource();
    }
    if (changes['colunas'] || changes['paginarColunas'] || changes['colunasPorGrupo']) {
      this.ajustarIndiceGrupo();
      this.atualizarDisplayedColumns();
    }
  }

  private inicializarDataSource(): void {
    this.dataSource = new MatTableDataSource(this.itens as any[]);
    this.dataSource.filterPredicate = (data: any, filter: string) => {
      const termo = filter.toLowerCase();
      return this.colunas.some(col => {
        const valor = data[col.campo];
        return valor !== null && valor !== undefined && 
               String(valor).toLowerCase().includes(termo);
      });
    };
    
    if (this.sort) {
      this.dataSource.sort = this.sort;
      this.aplicarOrdenacaoPadrao();
    }
    
    this.atualizarDisplayedColumns();
  }

  ngAfterViewInit(): void {
    if (this.dataSource && this.sort) {
      this.dataSource.sort = this.sort;
      this.aplicarOrdenacaoPadrao();
    }
  }

  /**
   * Aplica a ordenação padrão UMA ÚNICA VEZ. Em modo server-side
   * apenas seta o estado interno (combo/radio) — não chama
   * `this.sort.sort(...)` para evitar acionar o `(matSortChange)` e
   * com isso re-emitir `(ordenacaoAlterada)`. Em modo client-side
   * chama o sort normalmente para reordenar a `MatTableDataSource`.
   */
  private aplicarOrdenacaoPadrao(): void {
    if (this.ordenacaoInicializada) {
      return;
    }
    const colOrdenacao = this.colunas.find(c => c.ordenacaoPadrao);
    if (!colOrdenacao || !this.sort) {
      return;
    }
    const dir = colOrdenacao.ordenacaoDirecao || 'asc';
    this.ordenacaoColunaSelecionada = colOrdenacao.id;
    this.ordenacaoDesc = dir === 'desc';
    this.sort.disableClear = true;
    if (!this.ordenacaoServerSide) {
      this.sort.sort({ id: colOrdenacao.id, start: dir } as MatSortable);
      this.dataSource.sort = this.sort;
    }
    this.ordenacaoInicializada = true;
  }

  /**
   * Colunas elegíveis para o combo de ordenação:
   *  - server-side: apenas as que possuem `valorOrdenacaoBackend`.
   *  - client-side: todas com `ordenavel !== false`, `campo` e que não
   *                 sejam coluna de botão de ação.
   */
  get colunasOrdenaveis(): ListaColunaConfig[] {
    if (this.ordenacaoServerSide) {
      return this.colunas.filter(
        c => !!c.valorOrdenacaoBackend && c.ordenavel !== false,
      );
    }
    return this.colunas.filter(
      c => c.ordenavel !== false && c.modoCelula !== 'botaoAcao' && c.campo,
    );
  }

  /** Disparado pelo `(selectionChange)` do combo de ordenação. */
  aoMudarOrdenacaoCombo(idColuna: string): void {
    if (!idColuna) {
      return;
    }
    this.ordenacaoColunaSelecionada = idColuna;
    // Em modo client-side reordenamos localmente; em server-side o pai
    // recarrega a página e o backend retorna já ordenado.
    if (!this.ordenacaoServerSide && this.sort) {
      this.sort.sort({
        id: idColuna,
        start: this.ordenacaoDesc ? 'desc' : 'asc',
      } as MatSortable);
    }
    this.emitirOrdenacaoAlterada();
  }

  /** Disparado pelo `(change)` do radio asc/desc. */
  aoMudarDirecaoOrdenacao(novoDesc: boolean): void {
    this.ordenacaoDesc = novoDesc;
    if (
      !this.ordenacaoServerSide &&
      this.ordenacaoColunaSelecionada &&
      this.sort
    ) {
      this.sort.sort({
        id: this.ordenacaoColunaSelecionada,
        start: novoDesc ? 'desc' : 'asc',
      } as MatSortable);
    }
    this.emitirOrdenacaoAlterada();
  }

  /**
   * Mantém o combo/radio sincronizados quando o usuário clica diretamente
   * no header. Em modo server-side, também emite (ordenacaoAlterada) para
   * o pai recarregar a página.
   */
  aoMudarOrdenacaoSort(evento: Sort): void {
    if (evento.direction) {
      this.ordenacaoColunaSelecionada = evento.active;
      this.ordenacaoDesc = evento.direction === 'desc';
    } else {
      this.ordenacaoColunaSelecionada = '';
    }
    this.emitirOrdenacaoAlterada();
  }

  private emitirOrdenacaoAlterada(): void {
    if (!this.ordenacaoServerSide) {
      return;
    }
    const col = this.colunas.find(c => c.id === this.ordenacaoColunaSelecionada);
    this.ordenacaoAlterada.emit({
      idColuna: this.ordenacaoColunaSelecionada,
      valorBackend: col?.valorOrdenacaoBackend,
      desc: this.ordenacaoDesc,
    });
  }

  private atualizarDisplayedColumns(): void {
    const colunasVisiveis = this.colunasVisiveis;
    this.displayedColumns = [
      'acoes',
      ...colunasVisiveis.map(c => c.id)
    ];
  }

  get colunasVisiveis(): ListaColunaConfig[] {
    if (!this.paginarColunas || this.colunasPorGrupo < 1) {
      return this.colunas;
    }
    const inicio = this.indiceGrupoColuna * this.colunasPorGrupo;
    return this.colunas.slice(inicio, inicio + this.colunasPorGrupo);
  }

  get totalGruposColuna(): number {
    if (!this.paginarColunas || this.colunasPorGrupo < 1) {
      return 1;
    }
    return Math.max(1, Math.ceil(this.colunas.length / this.colunasPorGrupo));
  }

  get grupoColunaAtualRotulo(): string {
    return `${this.indiceGrupoColuna + 1} / ${this.totalGruposColuna}`;
  }

  get tamanhoPaginaSeguro(): number {
    const n = Math.floor(Number(this.tamanhoPagina));
    return Number.isFinite(n) && n >= 1 ? n : 10;
  }

  get paginaAtualSegura(): number {
    const n = Math.floor(Number(this.paginaAtual));
    return Number.isFinite(n) && n >= 1 ? n : 1;
  }

  aplicarFiltro(): void {
    this.dataSource.filter = this.filtroTextoInterno.trim().toLowerCase();
  }

  onPageEvent(e: PageEvent): void {
    if (!this.paginacaoClientSide) {
      this.paginacaoAlterada.emit({
        pagina: e.pageIndex + 1,
        tamanhoPagina: e.pageSize,
      });
    }
  }

  grupoColunaAnterior(): void {
    if (this.indiceGrupoColuna <= 0) {
      this.indiceGrupoColuna = this.totalGruposColuna - 1;
    } else {
      this.indiceGrupoColuna -= 1;
    }
    this.atualizarDisplayedColumns();
  }

  grupoColunaProximo(): void {
    if (this.indiceGrupoColuna >= this.totalGruposColuna - 1) {
      this.indiceGrupoColuna = 0;
    } else {
      this.indiceGrupoColuna += 1;
    }
    this.atualizarDisplayedColumns();
  }

  private ajustarIndiceGrupo(): void {
    if (this.indiceGrupoColuna >= this.totalGruposColuna) {
      this.indiceGrupoColuna = Math.max(0, this.totalGruposColuna - 1);
    }
  }

  obterValor(linha: unknown, col: ListaColunaConfig): unknown {
    if (linha && typeof linha === 'object' && col.campo in linha) {
      return (linha as Record<string, unknown>)[col.campo];
    }
    return '';
  }

  /**
   * Retorna o tooltip da célula (vazio quando não deve aparecer).
   * Regras (descritas em `ListaColunaConfig.campoTooltip`):
   *  - Sem `campoTooltip` configurado → vazio.
   *  - Valor do `campo` principal vazio/`null`/`undefined` → vazio
   *    (não mostra tooltip em célula sem conteúdo).
   *  - Valor do `campoTooltip` vazio/`null`/`undefined` → vazio.
   *  - Caso contrário → string do `campoTooltip`.
   */
  obterTooltipCelula(linha: unknown, col: ListaColunaConfig): string {
    if (!col.campoTooltip || !linha || typeof linha !== 'object') {
      return '';
    }
    const dados = linha as Record<string, unknown>;
    const valorPrincipal = dados[col.campo];
    if (
      valorPrincipal === null ||
      valorPrincipal === undefined ||
      String(valorPrincipal).trim() === ''
    ) {
      return '';
    }
    const valorTooltip = dados[col.campoTooltip];
    if (valorTooltip === null || valorTooltip === undefined) {
      return '';
    }
    const txt = String(valorTooltip).trim();
    return txt;
  }

  obterValorNumerico(linha: unknown, col: ListaColunaConfig): number | null {
    const v = this.obterValor(linha, col);
    if (typeof v === 'number' && !Number.isNaN(v)) {
      return v;
    }
    if (v === null || v === undefined || v === '') {
      return null;
    }
    const n = Number(v);
    return Number.isNaN(n) ? null : n;
  }

  formatarData(valor: unknown): string {
    if (!valor) return '';
    const date = new Date(valor as string);
    if (isNaN(date.getTime())) return String(valor);
    return date.toLocaleDateString('pt-BR');
  }

  formatarDataHora(valor: unknown): string {
    if (!valor) return '';
    const date = new Date(valor as string);
    if (isNaN(date.getTime())) return String(valor);
    const hora = date.toLocaleTimeString('pt-BR', {
      hour: '2-digit',
      minute: '2-digit', 
      second: '2-digit',
    });
    return `${date.toLocaleDateString('pt-BR')} ${hora}`;
  }

  formatarCpf(valor: unknown): string {
    if (!valor) return '';
    const cpf = String(valor).replace(/\D/g, '');
    if (cpf.length === 11) {
      return cpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
    }
    return String(valor);
  }

  mostrarBotaoAcao(linha: unknown, col: ListaColunaConfig): boolean {
    if (col.botaoAcaoConfig?.exibirQuando) {
      return col.botaoAcaoConfig.exibirQuando(linha);
    }
    return true;
  }

  onBotaoAcaoClick(linha: unknown, col: ListaColunaConfig, event: Event): void {
    event.stopPropagation();
    this.botaoAcaoClicado.emit({ linha, coluna: col });
  }

  /** Disparado pelo clique em um dos ícones do modo `'iconesImagens'`. */
  onIconeImagemClick(
    linha: unknown,
    col: ListaColunaConfig,
    iconeId: string,
    event: Event,
  ): void {
    event.stopPropagation();
    this.iconeImagemClicado.emit({ linha, coluna: col, iconeId });
  }

  /** trackBy para `*ngFor` da lista de ícones do modo `'iconesImagens'`. */
  trackIcone(_: number, item: { id: string }): string {
    return item.id;
  }

  executarAcao(linha: unknown, acao: ListaAcaoEngrenagem<any>, event: Event): void {
    event.stopPropagation();
    const st = acao.exibicao(linha);
    if (st !== 'habilitado') {
      return;
    }
    acao.aoExecutar(linha);
  }

  classeAlinhamento(col: ListaColunaConfig): string {
    switch (col.alinhamento) {
      case 'direita': return 'cel-direita';
      case 'centro': return 'cel-centro';
      default: return 'cel-esquerda';
    }
  }

  getRowClass(row: unknown): string {
    if (!this.corLinhaCondicao) return '';
    try {
      const evaluator = new Function('row', this.corLinhaCondicao);
      return evaluator(row) || '';
    } catch {
      return '';
    }
  }

  isOrdenavel(col: ListaColunaConfig): boolean {
    return col.ordenavel !== false;
  }

  trackCol(_: number, col: ListaColunaConfig): string {
    return col.id;
  }

  trackAcao(_: number, acao: ListaAcaoEngrenagem<any>): string {
    return acao.id;
  }

  /**
   * Habilita o botão da engrenagem só quando existir ao menos uma ação
   * em estado `habilitado` para a linha (itens `oculto` não contam;
   * `desabilitado` sozinho deixa o menu sem ação utilizável).
   */
  temAcaoEngrenagemDisponivel(linha: unknown): boolean {
    if (!this.acoesEngrenagem?.length) {
      return false;
    }
    return this.acoesEngrenagem.some((a) => a.exibicao(linha) === 'habilitado');
  }

  // ========== EXPORTAÇÃO ==========

  /**
   * Abre diálogo para o usuário escolher o que exportar (página atual ou todos).
   * Se escolher "todos", emite o evento `exportarTodosSolicitado` para que o
   * componente pai busque todos os dados e chame `exportarDados()`.
   */
  abrirDialogoExportacao(nomeArquivo: string, autor = 'CTF'): void {
    const dialogData: ExportacaoDialogData = {
      totalPagina: this.dataSource.filteredData.length,
      totalRegistros: this.totalRegistros,
    };

    const dialogRef = this.dialog.open(ExportacaoDialogComponent, {
      width: '400px',
      data: dialogData,
    });

    dialogRef.afterClosed().subscribe((result: ExportacaoDialogResult | undefined) => {
      if (!result) return;

      if (result.tipo === 'pagina') {
        if (result.formato === 'xlsx') {
          this.exportarExcel(nomeArquivo, autor);
        } else {
          this.exportarCsv(nomeArquivo, autor);
        }
      } else {
        this.exportarTodosSolicitado.emit({
          nomeArquivo,
          autor,
          formato: result.formato,
        });
      }
    });
  }

  /**
   * Exporta os dados fornecidos (usado quando o componente pai busca todos os dados).
   */
  exportarDados(dados: unknown[], nomeArquivo: string, autor = 'CTF', formato: 'xlsx' | 'csv' = 'xlsx'): void {
    if (formato === 'xlsx') {
      this.exportarDadosComEstilo(dados, nomeArquivo, autor);
    } else {
      this.exportarDadosCsv(dados, nomeArquivo);
    }
  }

  /**
   * Exporta a página atual para Excel.
   */
  exportarExcel(nomeArquivo: string, autor = 'CTF'): void {
    this.exportarDadosComEstilo(this.dataSource.filteredData, nomeArquivo, autor);
  }

  /**
   * Exporta a página atual para CSV.
   */
  exportarCsv(nomeArquivo: string, autor = 'CTF'): void {
    if (this.exporter) {
      this.exporter.exportTable('csv', { fileName: nomeArquivo, Props: { Author: autor } });
    }
  }

  private exportarDadosComEstilo(dados: unknown[], nomeArquivo: string, autor: string): void {
    const colunasExport = this.colunas.filter(c => c.modoCelula !== 'botaoAcao');
    const headers = colunasExport.map(c => c.titulo);
    
    const linhas = dados.map(row => {
      return colunasExport.map(col => this.formatarValorExport(row, col));
    });

    const ws = XLSX.utils.aoa_to_sheet([headers, ...linhas]);

    const headerStyle = {
      font: { bold: true, color: { rgb: '151515' }, sz: 11 },
      fill: { fgColor: { rgb: 'E6E6E6' } },
      alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
      border: {
        top: { style: 'thin', color: { rgb: 'D3D3D3' } },
        bottom: { style: 'thin', color: { rgb: 'D3D3D3' } },
        left: { style: 'thin', color: { rgb: 'D3D3D3' } },
        right: { style: 'thin', color: { rgb: 'D3D3D3' } },
      },
    };

    const cellStyle = {
      alignment: { vertical: 'top', wrapText: true },
      border: {
        top: { style: 'thin', color: { rgb: 'D3D3D3' } },
        bottom: { style: 'thin', color: { rgb: 'D3D3D3' } },
        left: { style: 'thin', color: { rgb: 'D3D3D3' } },
        right: { style: 'thin', color: { rgb: 'D3D3D3' } },
      },
    };

    for (let i = 0; i < headers.length; i++) {
      const cellRef = XLSX.utils.encode_cell({ r: 0, c: i });
      if (!ws[cellRef]) ws[cellRef] = { t: 's', v: '' };
      ws[cellRef].s = headerStyle;
    }

    for (let r = 0; r < linhas.length; r++) {
      const isEven = r % 2 === 0;
      const rowStyle = {
        ...cellStyle,
        fill: { fgColor: { rgb: isEven ? 'FFFFFF' : 'F5F5F5' } },
      };
      for (let c = 0; c < headers.length; c++) {
        const cellRef = XLSX.utils.encode_cell({ r: r + 1, c: c });
        if (!ws[cellRef]) ws[cellRef] = { t: 's', v: '' };
        ws[cellRef].s = rowStyle;
      }
    }

    const colWidths = colunasExport.map(col => ({
      wch: col.larguraExcel || this.getLarguraPadrao(col),
    }));
    ws['!cols'] = colWidths;

    const wb = XLSX.utils.book_new();
    wb.Props = { Title: nomeArquivo, Author: autor, CreatedDate: new Date() };
    XLSX.utils.book_append_sheet(wb, ws, 'Dados');
    XLSX.writeFile(wb, `${nomeArquivo}.xlsx`);
  }

  private exportarDadosCsv(dados: unknown[], nomeArquivo: string): void {
    const colunasExport = this.colunas.filter(c => c.modoCelula !== 'botaoAcao');
    const headers = colunasExport.map(c => c.titulo);
    
    const linhas = dados.map(row => {
      return colunasExport.map(col => {
        const valor = this.formatarValorExport(row, col);
        if (valor.includes(',') || valor.includes('"') || valor.includes('\n')) {
          return `"${valor.replace(/"/g, '""')}"`;
        }
        return valor;
      }).join(',');
    });

    const csv = [headers.join(','), ...linhas].join('\n');
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${nomeArquivo}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  }

  private formatarValorExport(row: unknown, col: ListaColunaConfig): string {
    const valor = this.obterValor(row, col);
    if (valor === null || valor === undefined || valor === '') return '';

    switch (col.modoCelula) {
      case 'moeda':
        return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(valor));
      case 'litros':
        return `${new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(valor))} L`;
      case 'simNaoCor':
        return valor === true ? 'Sim' : valor === false ? 'Não' : '';
      case 'data':
        return this.formatarData(valor);
      case 'dataHora':
        return this.formatarDataHora(valor);
      case 'cpf':
        return this.formatarCpf(valor);
      default:
        if (col.formatoNumero && typeof valor === 'number') {
          return new Intl.NumberFormat('pt-BR').format(valor);
        }
        return String(valor);
    }
  }

  private getLarguraPadrao(col: ListaColunaConfig): number {
    switch (col.modoCelula) {
      case 'moeda': return 15;
      case 'litros': return 14;
      case 'data': return 12;
      case 'dataHora': return 18;
      case 'cpf': return 15;
      default: return 20;
    }
  }
}
